/**
 * Module: lib/copilot/hooks/useChat
 * Responsibility: Drives the active copilot turn — reducer state machine
 * (idle → sending → streaming → done/error), thin wrapper around
 * CopilotClient.chat. Exposes transcript, status, and action callbacks.
 *
 * The CopilotClient reads the auth cookie on every request via getAccessToken,
 * so this hook does not memoise the JWT.
 */

import { useCallback, useMemo, useReducer, useRef } from 'react'
import {
  AgentMismatchError,
  CopilotClient,
  CopilotError,
  PlanConflictError,
  PlanForbiddenError,
  PlanInvalidError,
  PlanStaleError
} from '../CopilotClient'
import type { CopilotSSEEvent } from '../events'
import type { PlanValidationIssue, PlanView } from '../plans'
import type { ParamEdits } from '../planParams'
import { handoffNotice, type ChatRouting } from '../agents'
import { resolveToolCopy } from '../toolCopy'
import { recordToolDecision } from '../../telemetry/toolDecision'
import { errorToast } from '../../utils/toasters'

export type TranscriptEntry =
  | {
      kind: 'user'
      text: string
      images?: string[]
      /** Epoch ms when the message was sent (client clock); omitted on replayed history without backend times. */ sentAt?: number
    }
  | {
      kind: 'assistant'
      text: string
      inFlight: boolean
      /** Persisted message id — set by `message.saved` or on replay; absent until stored (refusals never get one). */
      messageId?: string
      /**
       * Per-turn token usage. Populated as `usage.turn` events arrive during
       * the turn and preserved after the bubble stops being in-flight. Absent
       * on historical entries if the turn predates usage emission.
       */
      usage?: { input: number; output: number }
      /** Written by the plan engine on its own (a branch action or readout), not in reply to the merchant. */
      automatic?: boolean
    }
  | {
      kind: 'tool'
      toolName: string
      status: 'running' | 'done'
      summary: string
      toolUseId?: string
      input?: unknown
      output?: unknown
    }
  | {
      kind: 'confirm'
      toolName: string
      inputPreview: unknown
      confirmToken: string
      status: 'pending' | 'approved' | 'rejected'
    }
  | { kind: 'error'; code: string; message: string }
  /** A system line, e.g. why the thread restarted after an @-mention handoff. */
  | { kind: 'notice'; text: string }
  /** A CRO plan card; the latest PlanView lives in `plans[planId]`. */
  | { kind: 'plan'; planId: string }

export type ChatStatus = 'idle' | 'sending' | 'streaming' | 'error'

interface UseChatInit {
  readonly baseUrl: string
  readonly getAccessToken: () => string
  /**
   * Optional callback invoked when a copilot call returns 401. Used to
   * refresh the merchant JWT so the failing request can be retried.
   * Forwarded as-is to the underlying CopilotClient.
   */
  readonly onUnauthorized?: () => Promise<void> | void
  readonly initialConversationId?: string
}

interface ChatState {
  readonly status: ChatStatus
  readonly transcript: TranscriptEntry[]
  readonly conversationId: string
  /**
   * Cumulative token counts for the current conversation. Incremented by
   * `usage.turn` events during live turns and seeded on rehydrate from
   * `GET /conversation`'s `total_input_tokens` / `total_output_tokens`.
   */
  readonly totalTokens: { input: number; output: number }
  /** Latest view of every plan in this conversation, by plan id. */
  readonly plans?: Readonly<Record<string, PlanView>>
}

export type PlanActionFailure = 'stale' | 'forbidden' | 'conflict' | 'invalid' | 'not_found' | 'failed'

export type PlanActionResult =
  | { readonly ok: true }
  | {
      readonly ok: false
      readonly reason: PlanActionFailure
      /** What the server rejected, on `invalid`. */
      readonly issues?: readonly PlanValidationIssue[]
    }

/** What a plan card can ask for. Approve and undo resolve once their progress stream ends. */
export interface PlanActions {
  readonly approve: (planId: string) => Promise<PlanActionResult>
  /** One edit call: items to remove (dependents go too) and changed bounded numbers. */
  readonly edit: (planId: string, removeItemIds: readonly string[], params?: ParamEdits) => Promise<PlanActionResult>
  readonly cancel: (planId: string) => Promise<PlanActionResult>
  readonly stopMonitoring: (planId: string) => Promise<PlanActionResult>
  readonly undo: (planId: string, itemIds: readonly string[] | 'all') => Promise<PlanActionResult>
}

const EMPTY_PLANS: Readonly<Record<string, PlanView>> = {}

type Action =
  | { type: 'send_start'; userEntry: TranscriptEntry }
  | { type: 'stream_event'; event: CopilotSSEEvent }
  | { type: 'send_done' }
  | { type: 'send_stopped' }
  | { type: 'conversation_reset_id' }
  | { type: 'send_error'; code: string; message: string }
  | {
      type: 'load_conversation'
      conversationId: string
      transcript: TranscriptEntry[]
      totalTokens?: { input: number; output: number }
      plans?: readonly PlanView[]
    }
  | { type: 'resolve_confirm'; confirmToken: string; decision: 'approved' | 'rejected' }
  | { type: 'handoff_started'; conversationId: string; notice: string }
  | { type: 'plan_upsert'; plan: PlanView }
  | { type: 'plan_locked'; planId: string; lockedItemIds: readonly string[] }

/** Current turn's in-flight assistant — tools and confirms insert immediately before it. */
function findInFlightAssistantIndex(transcript: readonly TranscriptEntry[]): number {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const entry = transcript[i]
    if (entry.kind === 'assistant' && entry.inFlight) return i
  }
  return -1
}

function insertBeforeInFlightAssistant(
  transcript: readonly TranscriptEntry[],
  entry: TranscriptEntry
): TranscriptEntry[] {
  const assistantIdx = findInFlightAssistantIndex(transcript)
  if (assistantIdx === -1) return [...transcript, entry]
  const next = [...transcript]
  next.splice(assistantIdx, 0, entry)
  return next
}

/**
 * Puts a plan card where its event arrived in the stream: text streamed so far
 * stays above the card, and later text goes to a fresh bubble below it.
 */
function placePlanEntry(transcript: readonly TranscriptEntry[], planId: string): TranscriptEntry[] {
  if (transcript.some(e => e.kind === 'plan' && e.planId === planId)) return [...transcript]

  const card: TranscriptEntry = { kind: 'plan', planId }
  const assistantIdx = findInFlightAssistantIndex(transcript)
  if (assistantIdx === -1) return [...transcript, card]

  const inFlight = transcript[assistantIdx] as Extract<TranscriptEntry, { kind: 'assistant' }>
  const before = transcript.slice(0, assistantIdx)
  const after = transcript.slice(assistantIdx + 1)
  const hasText = inFlight.text.length > 0
  const closed: TranscriptEntry[] = hasText ? [{ ...inFlight, inFlight: false }] : []
  const carriedUsage = !hasText && inFlight.usage ? { usage: inFlight.usage } : {}

  return [...before, ...closed, card, ...after, { kind: 'assistant', text: '', inFlight: true, ...carriedUsage }]
}

/** An empty bubble opened below a plan card that the turn never wrote into. */
function withoutEmptyBubbleAfterPlan(transcript: readonly TranscriptEntry[]): TranscriptEntry[] {
  return transcript.filter(
    (e, i) => !(e.kind === 'assistant' && e.text.length === 0 && transcript[i - 1]?.kind === 'plan')
  )
}

function planMap(plans: readonly PlanView[]): Readonly<Record<string, PlanView>> {
  return Object.fromEntries(plans.map(plan => [plan.id, plan]))
}

function indexOfLastUser(transcript: readonly TranscriptEntry[]): number {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    if (transcript[i].kind === 'user') return i
  }
  return -1
}

function transcriptAfterStopRollback(transcript: readonly TranscriptEntry[]): TranscriptEntry[] {
  const lastUserIdx = indexOfLastUser(transcript)
  if (lastUserIdx === -1) return [...transcript]
  return transcript.slice(0, lastUserIdx)
}

export function reducer(state: ChatState, action: Action): ChatState {
  switch (action.type) {
    case 'send_start':
      return {
        ...state,
        status: 'sending',
        transcript: [...state.transcript, action.userEntry, { kind: 'assistant', text: '', inFlight: true }]
      }
    case 'stream_event': {
      const next = [...state.transcript]
      const ev = action.event
      if (ev.type === 'delta.text') {
        for (let i = next.length - 1; i >= 0; i -= 1) {
          const entry = next[i]
          if (entry.kind === 'assistant' && entry.inFlight) {
            next[i] = { ...entry, text: entry.text + ev.text }
            break
          }
        }
      } else if (ev.type === 'tool_use.start') {
        const copy = resolveToolCopy(ev.tool)
        return {
          ...state,
          status: 'streaming',
          transcript: insertBeforeInFlightAssistant(next, {
            kind: 'tool',
            toolName: ev.tool,
            status: 'running',
            summary: copy.running,
            input: ev.input
          })
        }
      } else if (ev.type === 'tool_use.result_ma') {
        for (let i = next.length - 1; i >= 0; i -= 1) {
          const entry = next[i]
          if (entry.kind === 'tool' && entry.status === 'running') {
            const copy = resolveToolCopy(entry.toolName)
            const arr = Array.isArray(ev.content) ? ev.content : []
            next[i] = {
              ...entry,
              status: 'done',
              summary: copy.done(arr.length),
              toolUseId: ev.tool_use_id,
              output: ev.content
            }
            break
          }
        }
      } else if (ev.type === 'tool_use.result') {
        // Match by the tool name the event carries — with parallel tool calls,
        // "most recent running" would attach outputs to the wrong card. Fall
        // back to the most recent running tool only when the name misses.
        let matchIdx = -1
        for (let i = next.length - 1; i >= 0; i -= 1) {
          const entry = next[i]
          if (entry.kind === 'tool' && entry.status === 'running') {
            if (matchIdx === -1) matchIdx = i
            if (entry.toolName === ev.tool) {
              matchIdx = i
              break
            }
          }
        }
        if (matchIdx !== -1) {
          const entry = next[matchIdx] as Extract<TranscriptEntry, { kind: 'tool' }>
          const copy = resolveToolCopy(entry.toolName)
          const arr = Array.isArray(ev.output) ? ev.output : []
          next[matchIdx] = {
            ...entry,
            status: 'done',
            summary: copy.done(arr.length),
            output: ev.output
          }
        }
      } else if (ev.type === 'tool_confirm_required') {
        return {
          ...state,
          status: 'streaming',
          transcript: insertBeforeInFlightAssistant(next, {
            kind: 'confirm',
            toolName: ev.tool,
            inputPreview: ev.input_preview,
            confirmToken: ev.confirm_token,
            status: 'pending'
          })
        }
      } else if (ev.type === 'tool_rejected') {
        // Mark the last pending confirm for this tool as rejected
        for (let i = next.length - 1; i >= 0; i -= 1) {
          const entry = next[i]
          if (entry.kind === 'confirm' && entry.toolName === ev.tool && entry.status === 'pending') {
            next[i] = { ...entry, status: 'rejected' }
            break
          }
        }
      } else if (ev.type === 'plan.proposed' || ev.type === 'plan.updated') {
        const belongsHere = !ev.plan.conversation_id || ev.plan.conversation_id === state.conversationId
        return {
          ...state,
          status: 'streaming',
          plans: { ...(state.plans ?? EMPTY_PLANS), [ev.plan.id]: ev.plan },
          transcript: belongsHere ? placePlanEntry(next, ev.plan.id) : next
        }
      } else if (ev.type === 'message.saved') {
        // The turn's reply is the last assistant entry after the last user entry.
        for (let i = next.length - 1; i >= 0; i -= 1) {
          const entry = next[i]
          if (entry.kind === 'user') break
          if (entry.kind === 'assistant') {
            next[i] = { ...entry, messageId: ev.message_id }
            break
          }
        }
        return { ...state, transcript: next }
      } else if (ev.type === 'usage.turn') {
        // Accumulate onto the most recent inFlight assistant entry, if one exists.
        for (let i = next.length - 1; i >= 0; i -= 1) {
          const entry = next[i]
          if (entry.kind === 'assistant' && entry.inFlight) {
            const prev = entry.usage ?? { input: 0, output: 0 }
            next[i] = {
              ...entry,
              usage: {
                input: prev.input + ev.input_tokens,
                output: prev.output + ev.output_tokens
              }
            }
            break
          }
        }
        // Always bump cumulative — totals count tokens even if no bubble owns them yet.
        // Intentionally preserve state.status — unlike the shared fall-through
        // below, usage.turn can arrive after a terminal event (rare but valid)
        // and must not regress status back to "streaming".
        return {
          ...state,
          transcript: next,
          totalTokens: {
            input: state.totalTokens.input + ev.input_tokens,
            output: state.totalTokens.output + ev.output_tokens
          }
        }
      }
      return { ...state, status: 'streaming', transcript: next }
    }
    case 'send_done': {
      const next = state.transcript.map(e => (e.kind === 'assistant' ? { ...e, inFlight: false } : e))
      return { ...state, status: 'idle', transcript: withoutEmptyBubbleAfterPlan(next) }
    }
    case 'send_stopped': {
      if (state.status !== 'sending' && state.status !== 'streaming') return state
      const nextTranscript = transcriptAfterStopRollback(state.transcript)
      return {
        ...state,
        status: 'idle',
        transcript: nextTranscript,
        conversationId: nextTranscript.length === 0 ? crypto.randomUUID() : state.conversationId
      }
    }
    case 'conversation_reset_id':
      return { ...state, conversationId: crypto.randomUUID() }
    case 'send_error': {
      const next = [
        ...withoutEmptyBubbleAfterPlan(
          state.transcript.map(e => (e.kind === 'assistant' ? { ...e, inFlight: false } : e))
        ),
        { kind: 'error', code: action.code, message: action.message } as TranscriptEntry
      ]
      return { ...state, status: 'error', transcript: next }
    }
    case 'load_conversation':
      return {
        ...state,
        conversationId: action.conversationId,
        transcript: action.transcript,
        status: 'idle',
        totalTokens: action.totalTokens ?? state.totalTokens,
        plans: planMap(action.plans ?? [])
      }
    case 'resolve_confirm': {
      const next = state.transcript.map(e => {
        if (e.kind === 'confirm' && e.confirmToken === action.confirmToken) {
          return { ...e, status: action.decision }
        }
        return e
      })
      return { ...state, transcript: next }
    }
    case 'handoff_started':
      // The mentioned agent continues in a new conversation; the old thread is
      // left untouched server-side and stays reachable from history.
      return {
        ...state,
        status: 'idle',
        conversationId: action.conversationId,
        transcript: [{ kind: 'notice', text: action.notice }],
        totalTokens: { input: 0, output: 0 },
        plans: EMPTY_PLANS
      }
    case 'plan_upsert':
      return { ...state, plans: { ...(state.plans ?? EMPTY_PLANS), [action.plan.id]: action.plan } }
    case 'plan_locked': {
      const plan = state.plans?.[action.planId]
      if (!plan) return state
      const locked = new Set([...plan.locked_item_ids, ...action.lockedItemIds])
      const relocked: PlanView = {
        ...plan,
        can_approve: false,
        locked_item_ids: Array.from(locked),
        items: plan.items.map(item => (locked.has(item.id) ? { ...item, locked: true } : item))
      }
      return { ...state, plans: { ...state.plans, [plan.id]: relocked } }
    }
    default:
      return state
  }
}

export function useChat(init: UseChatInit) {
  // CopilotClient is created once per hook mount: baseUrl and getAccessToken are
  // expected to be fixed for the app shell lifetime (env + cookie reader).
  const client = useMemo(
    () =>
      new CopilotClient({
        baseUrl: init.baseUrl,
        getAccessToken: init.getAccessToken,
        onUnauthorized: init.onUnauthorized
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const [state, dispatch] = useReducer(reducer, {
    status: 'idle',
    transcript: [],
    conversationId: init.initialConversationId ?? crypto.randomUUID(),
    totalTokens: { input: 0, output: 0 },
    plans: EMPTY_PLANS
  })
  const controllerRef = useRef<AbortController | null>(null)
  const conversationIdRef = useRef(state.conversationId)
  const transcriptRef = useRef(state.transcript)
  const plansRef = useRef(state.plans ?? EMPTY_PLANS)
  conversationIdRef.current = state.conversationId
  transcriptRef.current = state.transcript
  plansRef.current = state.plans ?? EMPTY_PLANS

  // delta.text events can arrive far faster than the display refreshes; one
  // dispatch per event clones the transcript and re-renders every consumer at
  // network rate. Buffer deltas and flush at most once per animation frame —
  // non-delta events flush synchronously first so ordering is preserved.
  const pendingDeltaRef = useRef('')
  const deltaRafRef = useRef<number | null>(null)
  const flushPendingDelta = useCallback((): void => {
    if (deltaRafRef.current !== null) {
      cancelAnimationFrame(deltaRafRef.current)
      deltaRafRef.current = null
    }
    const text = pendingDeltaRef.current
    if (!text) return
    pendingDeltaRef.current = ''
    dispatch({ type: 'stream_event', event: { type: 'delta.text', text } })
  }, [])
  const queueDelta = useCallback(
    (text: string): void => {
      pendingDeltaRef.current += text
      if (deltaRafRef.current === null) {
        deltaRafRef.current = requestAnimationFrame(() => {
          deltaRafRef.current = null
          flushPendingDelta()
        })
      }
    },
    [flushPendingDelta]
  )

  const send = useCallback(
    async (message: string, images?: string[], routing?: ChatRouting): Promise<void> => {
      dispatch({
        type: 'send_start',
        userEntry: { kind: 'user', text: message, images, sentAt: Date.now() }
      })
      controllerRef.current?.abort()
      const controller = new AbortController()
      controllerRef.current = controller
      try {
        for await (const ev of client.chat(conversationIdRef.current, message, controller.signal, routing)) {
          if (ev.type === 'delta.text') {
            queueDelta(ev.text)
            continue
          }
          flushPendingDelta()
          if (ev.type === 'turn.error') {
            // Abort the fetch — the generator's early return only releases the
            // reader lock; without this the SSE connection stays open until GC.
            controller.abort()
            dispatch({
              type: 'send_error',
              code: ev.code ?? 'turn_error',
              message: ev.error
            })
            return
          }
          dispatch({ type: 'stream_event', event: ev })
        }
        flushPendingDelta()
        dispatch({ type: 'send_done' })
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return
        flushPendingDelta()
        // The mentioned agent does not own this conversation: continue in a new
        // one that names this one as its source. Only once — a handoff that
        // mismatches again falls through to the error below.
        if (err instanceof AgentMismatchError && routing?.agent && !routing.handoffFrom) {
          const handoffFrom = conversationIdRef.current
          const conversationId = crypto.randomUUID()
          conversationIdRef.current = conversationId
          dispatch({ type: 'handoff_started', conversationId, notice: handoffNotice(routing.agent) })
          await send(message, images, { ...routing, handoffFrom })
          return
        }
        if (err instanceof CopilotError && err.status === 404 && err.endpoint === 'chat') {
          const afterRollback = transcriptAfterStopRollback(transcriptRef.current)
          dispatch({ type: 'send_stopped' })
          if (afterRollback.length > 0) {
            dispatch({ type: 'conversation_reset_id' })
          }
          return
        }
        dispatch({
          type: 'send_error',
          code: 'connection_lost',
          message: err instanceof Error ? err.message : String(err)
        })
      }
    },
    [client, flushPendingDelta, queueDelta]
  )

  const stop = useCallback((): void => {
    controllerRef.current?.abort()
    const nextTranscript = transcriptAfterStopRollback(transcriptRef.current)
    const isFirstTurnStop = nextTranscript.length === 0
    if (!isFirstTurnStop) {
      void client.cancel(conversationIdRef.current).catch(() => {})
    }
    dispatch({ type: 'send_stopped' })
  }, [client])

  const loadConversation = useCallback(
    (
      conversationId: string,
      transcript: TranscriptEntry[],
      totalTokens?: { input: number; output: number },
      plans?: readonly PlanView[]
    ): void => {
      conversationIdRef.current = conversationId
      transcriptRef.current = transcript
      dispatch({ type: 'load_conversation', conversationId, transcript, totalTokens, plans })
    },
    []
  )

  const planActions = useMemo<PlanActions>(() => {
    const upsert = (plan: PlanView): void => dispatch({ type: 'plan_upsert', plan })

    const refresh = async (planId: string): Promise<void> => {
      try {
        upsert(await client.getPlan(planId))
      } catch {
        // keep the last view; the card still shows why the action failed
      }
    }

    const failure = async (planId: string, err: unknown): Promise<PlanActionResult> => {
      if (err instanceof PlanStaleError) {
        if (err.plan) upsert(err.plan)
        return { ok: false, reason: 'stale' }
      }
      if (err instanceof PlanForbiddenError) {
        dispatch({ type: 'plan_locked', planId, lockedItemIds: err.lockedItemIds })
        return { ok: false, reason: 'forbidden' }
      }
      if (err instanceof PlanInvalidError) return { ok: false, reason: 'invalid', issues: err.issues }
      if (err instanceof CopilotError && err.status === 404) return { ok: false, reason: 'not_found' }

      await refresh(planId)

      return { ok: false, reason: err instanceof PlanConflictError ? 'conflict' : 'failed' }
    }

    const stream = async (planId: string, views: AsyncGenerator<PlanView>): Promise<PlanActionResult> => {
      let last: PlanView | undefined
      try {
        for await (const view of views) {
          last = view
          upsert(view)
        }
      } catch (err) {
        return failure(planId, err)
      }
      // The stream closed while the engine was still working: read where it got to.
      if (!last || last.status === 'APPROVED' || last.status === 'EXECUTING') await refresh(planId)

      return { ok: true }
    }

    const single = async (planId: string, request: () => Promise<PlanView>): Promise<PlanActionResult> => {
      try {
        upsert(await request())
      } catch (err) {
        return failure(planId, err)
      }

      return { ok: true }
    }

    const versionOf = (planId: string): number => plansRef.current[planId]?.version ?? 0

    return {
      approve: planId => stream(planId, client.approvePlan(planId, versionOf(planId))),
      undo: (planId, itemIds) => stream(planId, client.undoPlan(planId, itemIds)),
      edit: (planId, removeItemIds, params) =>
        single(planId, () =>
          client.editPlan(planId, {
            version: versionOf(planId),
            removeItemIds,
            ...(params && Object.keys(params).length > 0 ? { params } : {})
          })
        ),
      cancel: planId => single(planId, () => client.cancelPlan(planId)),
      stopMonitoring: planId => single(planId, () => client.stopPlanMonitoring(planId))
    }
  }, [client])

  const confirm = useCallback(
    async (confirmToken: string, decision: 'approve' | 'reject'): Promise<void> => {
      const entry = transcriptRef.current.find(e => e.kind === 'confirm' && e.confirmToken === confirmToken)
      const toolName = entry?.kind === 'confirm' ? entry.toolName : confirmToken
      try {
        await client.confirm(confirmToken, decision)
      } catch {
        // The server never received the decision — leave the card pending so the
        // merchant can retry. Resolving optimistically here would show a tool as
        // approved that will never run.
        errorToast('Flo-AI could not record your decision. Please try again.')
        return
      }
      dispatch({
        type: 'resolve_confirm',
        confirmToken,
        decision: decision === 'approve' ? 'approved' : 'rejected'
      })
      recordToolDecision(toolName, decision)
    },
    [client]
  )

  return useMemo(
    () => ({
      status: state.status,
      transcript: state.transcript,
      conversationId: state.conversationId,
      totalTokens: state.totalTokens,
      plans: state.plans ?? EMPTY_PLANS,
      planActions,
      send,
      stop,
      loadConversation,
      confirm
    }),
    [
      state.status,
      state.transcript,
      state.conversationId,
      state.totalTokens,
      state.plans,
      planActions,
      send,
      stop,
      loadConversation,
      confirm
    ]
  )
}
